import { Country, GameData } from '../types';

/**
 * PHASE 2 & 3 IMPLEMENTATION
 * 
 * In a real Node environment, this would be a build script using 'world-countries'.
 * Here, we simulate the output of that script with a hardcoded, rich subset of data
 * to ensure the app works immediately in the browser without backend build steps.
 */

let cachedData: GameData | null = null;

// Helper to generate flag URL
const getFlagUrl = (iso2: string) => `https://flagcdn.com/w80/${iso2.toLowerCase()}.png`;

// This raw data mimics what `scripts/build-data.ts` would output to `countries.canonical.json`.
// We include ~30 diverse countries to test all game mechanics (borders, regions, naming).
const RAW_COUNTRIES = [
  { n: "United States", i2: "US", i3: "USA", r: "Americas", s: "North America", b: ["CAN", "MEX"], a: ["USA", "America"] },
  { n: "Canada", i2: "CA", i3: "CAN", r: "Americas", s: "North America", b: ["USA"], a: [] },
  { n: "Mexico", i2: "MX", i3: "MEX", r: "Americas", s: "North America", b: ["USA", "BLZ", "GTM"], a: ["United Mexican States"] },
  { n: "Brazil", i2: "BR", i3: "BRA", r: "Americas", s: "South America", b: ["ARG", "BOL", "COL", "GUF", "GUY", "PRY", "PER", "SUR", "URY", "VEN"], a: ["Brasil"] },
  { n: "Argentina", i2: "AR", i3: "ARG", r: "Americas", s: "South America", b: ["BOL", "BRA", "CHL", "PRY", "URY"], a: [] },
  { n: "United Kingdom", i2: "GB", i3: "GBR", r: "Europe", s: "Northern Europe", b: ["IRL"], a: ["UK", "Great Britain"] },
  { n: "France", i2: "FR", i3: "FRA", r: "Europe", s: "Western Europe", b: ["AND", "BEL", "DEU", "ITA", "LUX", "MCO", "ESP", "CHE"], a: ["French Republic"] },
  { n: "Germany", i2: "DE", i3: "DEU", r: "Europe", s: "Western Europe", b: ["AUT", "BEL", "CZE", "DNK", "FRA", "LUX", "NLD", "POL", "CHE"], a: ["Deutschland"] },
  { n: "Italy", i2: "IT", i3: "ITA", r: "Europe", s: "Southern Europe", b: ["AUT", "FRA", "SVN", "CHE", "SMR", "VAT"], a: ["Italia"] },
  { n: "Spain", i2: "ES", i3: "ESP", r: "Europe", s: "Southern Europe", b: ["AND", "FRA", "PRT", "MAR"], a: ["España"] },
  { n: "China", i2: "CN", i3: "CHN", r: "Asia", s: "Eastern Asia", b: ["AFG", "BTN", "MMR", "IND", "KAZ", "PRK", "KGZ", "LAO", "MNG", "NPL", "PAK", "RUS", "TJK", "VNM"], a: ["PRC"] },
  { n: "Hong Kong", i2: "HK", i3: "HKG", r: "Asia", s: "Eastern Asia", b: ["CHN"], a: ["Hong Kong SAR", "Hong Kong Special Administrative Region"] },
  { n: "Japan", i2: "JP", i3: "JPN", r: "Asia", s: "Eastern Asia", b: [], a: ["Nippon"] },
  { n: "India", i2: "IN", i3: "IND", r: "Asia", s: "Southern Asia", b: ["BGD", "BTN", "MMR", "CHN", "NPL", "PAK"], a: ["Bharat"] },
  { n: "Australia", i2: "AU", i3: "AUS", r: "Oceania", s: "Australia and New Zealand", b: [], a: [] },
  { n: "New Zealand", i2: "NZ", i3: "NZL", r: "Oceania", s: "Australia and New Zealand", b: [], a: ["Aotearoa"] },
  { n: "South Africa", i2: "ZA", i3: "ZAF", r: "Africa", s: "Southern Africa", b: ["BWA", "LSO", "MOZ", "NAM", "SWZ", "ZWE"], a: ["RSA"] },
  { n: "Egypt", i2: "EG", i3: "EGY", r: "Africa", s: "Northern Africa", b: ["ISR", "LBY", "SDN"], a: [] },
  { n: "Kenya", i2: "KE", i3: "KEN", r: "Africa", s: "Eastern Africa", b: ["ETH", "SOM", "SSD", "TZA", "UGA"], a: [] },
  { n: "Russia", i2: "RU", i3: "RUS", r: "Europe", s: "Eastern Europe", b: ["AZE", "BLR", "CHN", "EST", "FIN", "GEO", "KAZ", "PRK", "LVA", "LTU", "MNG", "NOR", "POL", "UKR"], a: ["Russian Federation"] },
  { n: "Turkey", i2: "TR", i3: "TUR", r: "Asia", s: "Western Asia", b: ["ARM", "AZE", "BGR", "GEO", "GRC", "IRN", "IRQ", "SYR"], a: ["Turkiye"] },
  { n: "Sweden", i2: "SE", i3: "SWE", r: "Europe", s: "Northern Europe", b: ["FIN", "NOR"], a: ["Sverige"] },
  { n: "Norway", i2: "NO", i3: "NOR", r: "Europe", s: "Northern Europe", b: ["FIN", "SWE", "RUS"], a: ["Norge"] },
  { n: "Switzerland", i2: "CH", i3: "CHE", r: "Europe", s: "Western Europe", b: ["AUT", "FRA", "ITA", "LIE", "DEU"], a: ["Confoederatio Helvetica"] },
  { n: "Poland", i2: "PL", i3: "POL", r: "Europe", s: "Eastern Europe", b: ["BLR", "CZE", "DEU", "LTU", "RUS", "SVK", "UKR"], a: ["Polska"] },
  { n: "Thailand", i2: "TH", i3: "THA", r: "Asia", s: "South-Eastern Asia", b: ["MMR", "KHM", "LAO", "MYS"], a: ["Siam"] },
  { n: "Vietnam", i2: "VN", i3: "VNM", r: "Asia", s: "South-Eastern Asia", b: ["KHM", "CHN", "LAO"], a: [] },
  { n: "Indonesia", i2: "ID", i3: "IDN", r: "Asia", s: "South-Eastern Asia", b: ["TLS", "MYS", "PNG"], a: [] },
  { n: "Saudi Arabia", i2: "SA", i3: "SAU", r: "Asia", s: "Western Asia", b: ["IRQ", "JOR", "KWT", "OMN", "QAT", "ARE", "YEM"], a: ["KSA"] },
  { n: "Israel", i2: "IL", i3: "ISR", r: "Asia", s: "Western Asia", b: ["EGY", "JOR", "LBN", "SYR"], a: [] },
  { n: "Chile", i2: "CL", i3: "CHL", r: "Americas", s: "South America", b: ["ARG", "BOL", "PER"], a: [] },
  { n: "Portugal", i2: "PT", i3: "PRT", r: "Europe", s: "Southern Europe", b: ["ESP"], a: [] },
  { n: "Ireland", i2: "IE", i3: "IRL", r: "Europe", s: "Northern Europe", b: ["GBR"], a: [] },
  { n: "Netherlands", i2: "NL", i3: "NLD", r: "Europe", s: "Western Europe", b: ["BEL", "DEU"], a: [] },
  { n: "Belgium", i2: "BE", i3: "BEL", r: "Europe", s: "Western Europe", b: ["FRA", "NLD", "DEU", "LUX"], a: [] },
  { n: "Austria", i2: "AT", i3: "AUT", r: "Europe", s: "Western Europe", b: ["CZE", "DEU", "HUN", "ITA", "LIE", "SVK", "SVN", "CHE"], a: [] },
  { n: "Czechia", i2: "CZ", i3: "CZE", r: "Europe", s: "Eastern Europe", b: ["AUT", "DEU", "POL", "SVK"], a: ["Czech Republic"] },
  { n: "Slovakia", i2: "SK", i3: "SVK", r: "Europe", s: "Eastern Europe", b: ["AUT", "CZE", "HUN", "POL", "UKR"], a: [] },
  { n: "Hungary", i2: "HU", i3: "HUN", r: "Europe", s: "Eastern Europe", b: ["AUT", "HRV", "ROU", "SRB", "SVK", "SVN", "UKR"], a: [] },
  { n: "Romania", i2: "RO", i3: "ROU", r: "Europe", s: "Eastern Europe", b: ["BGR", "HUN", "MDA", "SRB", "UKR"], a: [] },
  { n: "Bulgaria", i2: "BG", i3: "BGR", r: "Europe", s: "Eastern Europe", b: ["GRC", "MKD", "ROU", "SRB", "TUR"], a: [] },
  { n: "Greece", i2: "GR", i3: "GRC", r: "Europe", s: "Southern Europe", b: ["ALB", "BGR", "MKD", "TUR"], a: [] },
  { n: "Cyprus", i2: "CY", i3: "CYP", r: "Europe", s: "Southern Europe", b: [], a: [] },
  { n: "Malta", i2: "MT", i3: "MLT", r: "Europe", s: "Southern Europe", b: [], a: [] },
  { n: "Serbia", i2: "RS", i3: "SRB", r: "Europe", s: "Southern Europe", b: ["BIH", "BGR", "HRV", "HUN", "MNE", "MKD", "ROU"], a: [] },
  { n: "Croatia", i2: "HR", i3: "HRV", r: "Europe", s: "Southern Europe", b: ["BIH", "HUN", "MNE", "SRB", "SVN"], a: [] },
  { n: "Slovenia", i2: "SI", i3: "SVN", r: "Europe", s: "Southern Europe", b: ["AUT", "HRV", "ITA", "HUN"], a: [] },
  { n: "Bosnia and Herzegovina", i2: "BA", i3: "BIH", r: "Europe", s: "Southern Europe", b: ["HRV", "MNE", "SRB"], a: [] },
  { n: "Albania", i2: "AL", i3: "ALB", r: "Europe", s: "Southern Europe", b: ["GRC", "MNE", "MKD", "SRB"], a: [] },
  { n: "North Macedonia", i2: "MK", i3: "MKD", r: "Europe", s: "Southern Europe", b: ["ALB", "BGR", "GRC", "SRB"], a: ["Macedonia"] },
  { n: "Montenegro", i2: "ME", i3: "MNE", r: "Europe", s: "Southern Europe", b: ["ALB", "BIH", "HRV", "SRB"], a: [] },
  { n: "Iceland", i2: "IS", i3: "ISL", r: "Europe", s: "Northern Europe", b: [], a: [] },
  { n: "Denmark", i2: "DK", i3: "DNK", r: "Europe", s: "Northern Europe", b: ["DEU"], a: [] },
  { n: "Finland", i2: "FI", i3: "FIN", r: "Europe", s: "Northern Europe", b: ["NOR", "SWE", "RUS"], a: [] },
  { n: "Estonia", i2: "EE", i3: "EST", r: "Europe", s: "Northern Europe", b: ["LVA", "RUS"], a: [] },
  { n: "Latvia", i2: "LV", i3: "LVA", r: "Europe", s: "Northern Europe", b: ["BLR", "EST", "LTU", "RUS"], a: [] },
  { n: "Lithuania", i2: "LT", i3: "LTU", r: "Europe", s: "Northern Europe", b: ["BLR", "LVA", "POL", "RUS"], a: [] },
  { n: "Ukraine", i2: "UA", i3: "UKR", r: "Europe", s: "Eastern Europe", b: ["BLR", "HUN", "MDA", "POL", "ROU", "RUS", "SVK"], a: [] },
  { n: "Belarus", i2: "BY", i3: "BLR", r: "Europe", s: "Eastern Europe", b: ["LVA", "LTU", "POL", "RUS", "UKR"], a: [] },
  { n: "Moldova", i2: "MD", i3: "MDA", r: "Europe", s: "Eastern Europe", b: ["ROU", "UKR"], a: [] },
  { n: "Georgia", i2: "GE", i3: "GEO", r: "Asia", s: "Western Asia", b: ["ARM", "AZE", "RUS", "TUR"], a: [] },
  { n: "Armenia", i2: "AM", i3: "ARM", r: "Asia", s: "Western Asia", b: ["AZE", "GEO", "IRN", "TUR"], a: [] },
  { n: "Azerbaijan", i2: "AZ", i3: "AZE", r: "Asia", s: "Western Asia", b: ["ARM", "GEO", "IRN", "RUS", "TUR"], a: [] },
  { n: "Iran", i2: "IR", i3: "IRN", r: "Asia", s: "Southern Asia", b: ["AFG", "ARM", "AZE", "IRQ", "PAK", "TUR", "TKM"], a: [] },
  { n: "Iraq", i2: "IQ", i3: "IRQ", r: "Asia", s: "Western Asia", b: ["IRN", "JOR", "KWT", "SAU", "SYR", "TUR"], a: [] },
  { n: "Syria", i2: "SY", i3: "SYR", r: "Asia", s: "Western Asia", b: ["IRQ", "ISR", "JOR", "LBN", "TUR"], a: [] },
  { n: "Lebanon", i2: "LB", i3: "LBN", r: "Asia", s: "Western Asia", b: ["ISR", "SYR"], a: [] },
  { n: "Jordan", i2: "JO", i3: "JOR", r: "Asia", s: "Western Asia", b: ["IRQ", "ISR", "SAU", "SYR"], a: [] },
  { n: "Kuwait", i2: "KW", i3: "KWT", r: "Asia", s: "Western Asia", b: ["IRQ", "SAU"], a: [] },
  { n: "Qatar", i2: "QA", i3: "QAT", r: "Asia", s: "Western Asia", b: ["SAU"], a: [] },
  { n: "United Arab Emirates", i2: "AE", i3: "ARE", r: "Asia", s: "Western Asia", b: ["OMN", "SAU"], a: ["UAE"] },
  { n: "Oman", i2: "OM", i3: "OMN", r: "Asia", s: "Western Asia", b: ["SAU", "ARE", "YEM"], a: [] },
  { n: "Yemen", i2: "YE", i3: "YEM", r: "Asia", s: "Western Asia", b: ["OMN", "SAU"], a: [] },
  { n: "Pakistan", i2: "PK", i3: "PAK", r: "Asia", s: "Southern Asia", b: ["AFG", "CHN", "IND", "IRN"], a: [] },
  { n: "Afghanistan", i2: "AF", i3: "AFG", r: "Asia", s: "Southern Asia", b: ["IRN", "PAK", "TJK", "TKM", "UZB", "CHN"], a: [] },
  { n: "Bangladesh", i2: "BD", i3: "BGD", r: "Asia", s: "Southern Asia", b: ["IND", "MMR"], a: [] },
  { n: "Sri Lanka", i2: "LK", i3: "LKA", r: "Asia", s: "Southern Asia", b: [], a: [] },
  { n: "Nepal", i2: "NP", i3: "NPL", r: "Asia", s: "Southern Asia", b: ["CHN", "IND"], a: [] },
  { n: "Bhutan", i2: "BT", i3: "BTN", r: "Asia", s: "Southern Asia", b: ["CHN", "IND"], a: [] },
  { n: "Myanmar", i2: "MM", i3: "MMR", r: "Asia", s: "South-Eastern Asia", b: ["BGD", "CHN", "IND", "LAO", "THA"], a: [] },
  { n: "Cambodia", i2: "KH", i3: "KHM", r: "Asia", s: "South-Eastern Asia", b: ["LAO", "THA", "VNM"], a: [] },
  { n: "Laos", i2: "LA", i3: "LAO", r: "Asia", s: "South-Eastern Asia", b: ["KHM", "CHN", "MMR", "THA", "VNM"], a: [] },
  { n: "Malaysia", i2: "MY", i3: "MYS", r: "Asia", s: "South-Eastern Asia", b: ["IDN", "THA", "BRN"], a: [] },
  { n: "Singapore", i2: "SG", i3: "SGP", r: "Asia", s: "South-Eastern Asia", b: [], a: [] },
  { n: "Philippines", i2: "PH", i3: "PHL", r: "Asia", s: "South-Eastern Asia", b: [], a: [] },
  { n: "South Korea", i2: "KR", i3: "KOR", r: "Asia", s: "Eastern Asia", b: ["PRK"], a: [] },
  { n: "North Korea", i2: "KP", i3: "PRK", r: "Asia", s: "Eastern Asia", b: ["CHN", "KOR", "RUS"], a: [] },
  { n: "Mongolia", i2: "MN", i3: "MNG", r: "Asia", s: "Eastern Asia", b: ["CHN", "RUS"], a: [] },
  { n: "Taiwan", i2: "TW", i3: "TWN", r: "Asia", s: "Eastern Asia", b: [], a: [] },
  { n: "Kazakhstan", i2: "KZ", i3: "KAZ", r: "Asia", s: "Central Asia", b: ["CHN", "KGZ", "RUS", "TKM", "UZB"], a: [] },
  { n: "Uzbekistan", i2: "UZ", i3: "UZB", r: "Asia", s: "Central Asia", b: ["AFG", "KAZ", "KGZ", "TJK", "TKM"], a: [] },
  { n: "Turkmenistan", i2: "TM", i3: "TKM", r: "Asia", s: "Central Asia", b: ["AFG", "IRN", "KAZ", "UZB"], a: [] },
  { n: "Kyrgyzstan", i2: "KG", i3: "KGZ", r: "Asia", s: "Central Asia", b: ["CHN", "KAZ", "TJK", "UZB"], a: [] },
  { n: "Tajikistan", i2: "TJ", i3: "TJK", r: "Asia", s: "Central Asia", b: ["AFG", "CHN", "KGZ", "UZB"], a: [] },
  { n: "Nigeria", i2: "NG", i3: "NGA", r: "Africa", s: "Western Africa", b: ["BEN", "CMR", "TCD", "NER"], a: [] },
  { n: "Ghana", i2: "GH", i3: "GHA", r: "Africa", s: "Western Africa", b: ["BFA", "CIV", "TGO"], a: [] },
  { n: "Ivory Coast", i2: "CI", i3: "CIV", r: "Africa", s: "Western Africa", b: ["BFA", "GHA", "GIN", "LBR", "MLI"], a: ["Côte d’Ivoire", "Cote d'Ivoire"] },
  { n: "Senegal", i2: "SN", i3: "SEN", r: "Africa", s: "Western Africa", b: ["GMB", "GIN", "GNB", "MLI", "MRT"], a: [] },
  { n: "Morocco", i2: "MA", i3: "MAR", r: "Africa", s: "Northern Africa", b: ["DZA"], a: [] },
  { n: "Algeria", i2: "DZ", i3: "DZA", r: "Africa", s: "Northern Africa", b: ["LBY", "MLI", "MRT", "NER", "TUN", "MAR"], a: [] },
  { n: "Tunisia", i2: "TN", i3: "TUN", r: "Africa", s: "Northern Africa", b: ["DZA", "LBY"], a: [] },
  { n: "Ethiopia", i2: "ET", i3: "ETH", r: "Africa", s: "Eastern Africa", b: ["DJI", "ERI", "KEN", "SOM", "SSD", "SDN"], a: [] },
  { n: "Eritrea", i2: "ER", i3: "ERI", r: "Africa", s: "Eastern Africa", b: ["DJI", "ETH", "SDN"], a: [] },
  { n: "Djibouti", i2: "DJ", i3: "DJI", r: "Africa", s: "Eastern Africa", b: ["ERI", "ETH", "SOM"], a: [] },
  { n: "Somalia", i2: "SO", i3: "SOM", r: "Africa", s: "Eastern Africa", b: ["DJI", "ETH", "KEN"], a: [] },
  { n: "Sudan", i2: "SD", i3: "SDN", r: "Africa", s: "Northern Africa", b: ["CAF", "TCD", "EGY", "ERI", "ETH", "LBY", "SSD"], a: [] },
  { n: "South Sudan", i2: "SS", i3: "SSD", r: "Africa", s: "Eastern Africa", b: ["CAF", "COD", "ETH", "KEN", "SDN", "UGA"], a: [] },
  { n: "Libya", i2: "LY", i3: "LBY", r: "Africa", s: "Northern Africa", b: ["DZA", "TCD", "EGY", "NER", "SDN", "TUN"], a: [] },
  { n: "Chad", i2: "TD", i3: "TCD", r: "Africa", s: "Middle Africa", b: ["CMR", "CAF", "LBY", "NER", "NGA", "SDN"], a: [] },
  { n: "Niger", i2: "NE", i3: "NER", r: "Africa", s: "Western Africa", b: ["DZA", "BEN", "BFA", "TCD", "LBY", "MLI", "NGA"], a: [] },
  { n: "Cameroon", i2: "CM", i3: "CMR", r: "Africa", s: "Middle Africa", b: ["NGA", "TCD", "CAF", "COG", "GNQ"], a: [] },
  { n: "Central African Republic", i2: "CF", i3: "CAF", r: "Africa", s: "Middle Africa", b: ["CMR", "TCD", "COD", "COG", "SSD", "SDN"], a: [] },
  { n: "Republic of the Congo", i2: "CG", i3: "COG", r: "Africa", s: "Middle Africa", b: ["AGO", "CMR", "CAF", "COD", "GAB"], a: [] },
  { n: "Gabon", i2: "GA", i3: "GAB", r: "Africa", s: "Middle Africa", b: ["CMR", "COG", "GNQ"], a: [] },
  { n: "Equatorial Guinea", i2: "GQ", i3: "GNQ", r: "Africa", s: "Middle Africa", b: ["CMR", "GAB"], a: [] },
  { n: "Rwanda", i2: "RW", i3: "RWA", r: "Africa", s: "Eastern Africa", b: ["BDI", "COD", "TZA", "UGA"], a: [] },
  { n: "Burundi", i2: "BI", i3: "BDI", r: "Africa", s: "Eastern Africa", b: ["COD", "RWA", "TZA"], a: [] },
  { n: "Malawi", i2: "MW", i3: "MWI", r: "Africa", s: "Eastern Africa", b: ["MOZ", "TZA", "ZMB"], a: [] },
  { n: "Lesotho", i2: "LS", i3: "LSO", r: "Africa", s: "Southern Africa", b: ["ZAF"], a: [] },
  { n: "Eswatini", i2: "SZ", i3: "SWZ", r: "Africa", s: "Southern Africa", b: ["MOZ", "ZAF"], a: ["Swaziland"] },
  { n: "Uganda", i2: "UG", i3: "UGA", r: "Africa", s: "Eastern Africa", b: ["COD", "KEN", "RWA", "SSD", "TZA"], a: [] },
  { n: "Tanzania", i2: "TZ", i3: "TZA", r: "Africa", s: "Eastern Africa", b: ["BDI", "COD", "KEN", "MWI", "MOZ", "RWA", "UGA", "ZMB"], a: [] },
  { n: "Democratic Republic of the Congo", i2: "CD", i3: "COD", r: "Africa", s: "Middle Africa", b: ["AGO", "BDI", "CAF", "COG", "RWA", "SSD", "TZA", "UGA", "ZMB"], a: ["DR Congo"] },
  { n: "Angola", i2: "AO", i3: "AGO", r: "Africa", s: "Middle Africa", b: ["COD", "COG", "ZMB", "NAM"], a: [] },
  { n: "Zambia", i2: "ZM", i3: "ZMB", r: "Africa", s: "Eastern Africa", b: ["AGO", "BWA", "COD", "MWI", "MOZ", "NAM", "TZA", "ZWE"], a: [] },
  { n: "Zimbabwe", i2: "ZW", i3: "ZWE", r: "Africa", s: "Eastern Africa", b: ["BWA", "MOZ", "ZAF", "ZMB"], a: [] },
  { n: "Botswana", i2: "BW", i3: "BWA", r: "Africa", s: "Southern Africa", b: ["NAM", "ZAF", "ZMB", "ZWE"], a: [] },
  { n: "Namibia", i2: "NA", i3: "NAM", r: "Africa", s: "Southern Africa", b: ["AGO", "BWA", "ZAF", "ZMB"], a: [] },
  { n: "Mozambique", i2: "MZ", i3: "MOZ", r: "Africa", s: "Eastern Africa", b: ["MWI", "ZAF", "SWZ", "TZA", "ZMB", "ZWE"], a: [] },
  { n: "Madagascar", i2: "MG", i3: "MDG", r: "Africa", s: "Eastern Africa", b: [], a: [] },
  { n: "Benin", i2: "BJ", i3: "BEN", r: "Africa", s: "Western Africa", b: ["BFA", "NER", "NGA", "TGO"], a: [] },
  { n: "Togo", i2: "TG", i3: "TGO", r: "Africa", s: "Western Africa", b: ["BEN", "BFA", "GHA"], a: [] },
  { n: "Burkina Faso", i2: "BF", i3: "BFA", r: "Africa", s: "Western Africa", b: ["BEN", "CIV", "GHA", "MLI", "NER", "TGO"], a: [] },
  { n: "Mali", i2: "ML", i3: "MLI", r: "Africa", s: "Western Africa", b: ["DZA", "BFA", "CIV", "GIN", "MRT", "NER", "SEN"], a: [] },
  { n: "Mauritania", i2: "MR", i3: "MRT", r: "Africa", s: "Western Africa", b: ["DZA", "MLI", "SEN"], a: [] },
  { n: "Guinea", i2: "GN", i3: "GIN", r: "Africa", s: "Western Africa", b: ["CIV", "GNB", "LBR", "MLI", "SEN", "SLE"], a: [] },
  { n: "Guinea-Bissau", i2: "GW", i3: "GNB", r: "Africa", s: "Western Africa", b: ["GIN", "SEN"], a: [] },
  { n: "Liberia", i2: "LR", i3: "LBR", r: "Africa", s: "Western Africa", b: ["CIV", "GIN", "SLE"], a: [] },
  { n: "Sierra Leone", i2: "SL", i3: "SLE", r: "Africa", s: "Western Africa", b: ["GIN", "LBR"], a: [] },
  { n: "Gambia", i2: "GM", i3: "GMB", r: "Africa", s: "Western Africa", b: ["SEN"], a: [] },
  { n: "Peru", i2: "PE", i3: "PER", r: "Americas", s: "South America", b: ["BOL", "BRA", "CHL", "COL", "ECU"], a: [] },
  { n: "Colombia", i2: "CO", i3: "COL", r: "Americas", s: "South America", b: ["BRA", "ECU", "PAN", "PER", "VEN"], a: [] },
  { n: "Venezuela", i2: "VE", i3: "VEN", r: "Americas", s: "South America", b: ["BRA", "COL", "GUY"], a: [] },
  { n: "Uruguay", i2: "UY", i3: "URY", r: "Americas", s: "South America", b: ["ARG", "BRA"], a: [] },
  { n: "Paraguay", i2: "PY", i3: "PRY", r: "Americas", s: "South America", b: ["ARG", "BOL", "BRA"], a: [] },
  { n: "Bolivia", i2: "BO", i3: "BOL", r: "Americas", s: "South America", b: ["ARG", "BRA", "CHL", "PRY", "PER"], a: [] },
  { n: "Ecuador", i2: "EC", i3: "ECU", r: "Americas", s: "South America", b: ["COL", "PER"], a: [] },
  { n: "Guyana", i2: "GY", i3: "GUY", r: "Americas", s: "South America", b: ["BRA", "SUR", "VEN"], a: [] },
  { n: "Suriname", i2: "SR", i3: "SUR", r: "Americas", s: "South America", b: ["BRA", "GUY"], a: [] },
  { n: "Panama", i2: "PA", i3: "PAN", r: "Americas", s: "Central America", b: ["COL", "CRI"], a: [] },
  { n: "Costa Rica", i2: "CR", i3: "CRI", r: "Americas", s: "Central America", b: ["NIC", "PAN"], a: [] },
  { n: "Nicaragua", i2: "NI", i3: "NIC", r: "Americas", s: "Central America", b: ["CRI", "HND"], a: [] },
  { n: "Honduras", i2: "HN", i3: "HND", r: "Americas", s: "Central America", b: ["GTM", "SLV", "NIC"], a: [] },
  { n: "Guatemala", i2: "GT", i3: "GTM", r: "Americas", s: "Central America", b: ["BLZ", "SLV", "HND", "MEX"], a: [] },
  { n: "Belize", i2: "BZ", i3: "BLZ", r: "Americas", s: "Central America", b: ["GTM", "MEX"], a: [] },
  { n: "El Salvador", i2: "SV", i3: "SLV", r: "Americas", s: "Central America", b: ["GTM", "HND"], a: [] },
  { n: "Cuba", i2: "CU", i3: "CUB", r: "Americas", s: "Caribbean", b: [], a: [] },
  { n: "Haiti", i2: "HT", i3: "HTI", r: "Americas", s: "Caribbean", b: ["DOM"], a: [] },
  { n: "Dominican Republic", i2: "DO", i3: "DOM", r: "Americas", s: "Caribbean", b: ["HTI"], a: [] },
  { n: "Jamaica", i2: "JM", i3: "JAM", r: "Americas", s: "Caribbean", b: [], a: [] },
  { n: "Bahamas", i2: "BS", i3: "BHS", r: "Americas", s: "Caribbean", b: [], a: [] },
  { n: "Andorra", i2: "AD", i3: "AND", r: "Europe", s: "Southern Europe", b: ["FRA", "ESP"], a: [] },
  { n: "Antigua and Barbuda", i2: "AG", i3: "ATG", r: "Americas", s: "Caribbean", b: [], a: [] },
  { n: "Bahrain", i2: "BH", i3: "BHR", r: "Asia", s: "Western Asia", b: [], a: [] },
  { n: "Barbados", i2: "BB", i3: "BRB", r: "Americas", s: "Caribbean", b: [], a: [] },
  { n: "Brunei", i2: "BN", i3: "BRN", r: "Asia", s: "South-Eastern Asia", b: ["MYS"], a: [] },
  { n: "Cape Verde", i2: "CV", i3: "CPV", r: "Africa", s: "Western Africa", b: [], a: ["Cabo Verde"] },
  { n: "Comoros", i2: "KM", i3: "COM", r: "Africa", s: "Eastern Africa", b: [], a: [] },
  { n: "Dominica", i2: "DM", i3: "DMA", r: "Americas", s: "Caribbean", b: [], a: [] },
  { n: "East Timor", i2: "TL", i3: "TLS", r: "Asia", s: "South-Eastern Asia", b: ["IDN"], a: ["Timor-Leste"] },
  { n: "Fiji", i2: "FJ", i3: "FJI", r: "Oceania", s: "Melanesia", b: [], a: [] },
  { n: "Grenada", i2: "GD", i3: "GRD", r: "Americas", s: "Caribbean", b: [], a: [] },
  { n: "Kiribati", i2: "KI", i3: "KIR", r: "Oceania", s: "Micronesia", b: [], a: [] },
  { n: "Kosovo", i2: "XK", i3: "XKX", r: "Europe", s: "Southern Europe", b: ["ALB", "MKD", "MNE", "SRB"], a: [] },
  { n: "Liechtenstein", i2: "LI", i3: "LIE", r: "Europe", s: "Western Europe", b: ["AUT", "CHE"], a: [] },
  { n: "Luxembourg", i2: "LU", i3: "LUX", r: "Europe", s: "Western Europe", b: ["BEL", "FRA", "DEU"], a: [] },
  { n: "Maldives", i2: "MV", i3: "MDV", r: "Asia", s: "Southern Asia", b: [], a: [] },
  { n: "Marshall Islands", i2: "MH", i3: "MHL", r: "Oceania", s: "Micronesia", b: [], a: [] },
  { n: "Mauritius", i2: "MU", i3: "MUS", r: "Africa", s: "Eastern Africa", b: [], a: [] },
  { n: "Micronesia", i2: "FM", i3: "FSM", r: "Oceania", s: "Micronesia", b: [], a: ["Federated States of Micronesia"] },
  { n: "Nauru", i2: "NR", i3: "NRU", r: "Oceania", s: "Micronesia", b: [], a: [] },
  { n: "Palau", i2: "PW", i3: "PLW", r: "Oceania", s: "Micronesia", b: [], a: [] },
  { n: "Palestine", i2: "PS", i3: "PSE", r: "Asia", s: "Western Asia", b: ["EGY", "ISR", "JOR"], a: ["State of Palestine"] },
  { n: "Papua New Guinea", i2: "PG", i3: "PNG", r: "Oceania", s: "Melanesia", b: ["IDN"], a: [] },
  { n: "Saint Kitts and Nevis", i2: "KN", i3: "KNA", r: "Americas", s: "Caribbean", b: [], a: [] },
  { n: "Saint Lucia", i2: "LC", i3: "LCA", r: "Americas", s: "Caribbean", b: [], a: [] },
  { n: "Saint Vincent and the Grenadines", i2: "VC", i3: "VCT", r: "Americas", s: "Caribbean", b: [], a: [] },
  { n: "Samoa", i2: "WS", i3: "WSM", r: "Oceania", s: "Polynesia", b: [], a: [] },
  { n: "San Marino", i2: "SM", i3: "SMR", r: "Europe", s: "Southern Europe", b: ["ITA"], a: [] },
  { n: "Sao Tome and Principe", i2: "ST", i3: "STP", r: "Africa", s: "Middle Africa", b: [], a: ["São Tomé and Príncipe"] },
  { n: "Seychelles", i2: "SC", i3: "SYC", r: "Africa", s: "Eastern Africa", b: [], a: [] },
  { n: "Solomon Islands", i2: "SB", i3: "SLB", r: "Oceania", s: "Melanesia", b: [], a: [] },
  { n: "Tonga", i2: "TO", i3: "TON", r: "Oceania", s: "Polynesia", b: [], a: [] },
  { n: "Trinidad and Tobago", i2: "TT", i3: "TTO", r: "Americas", s: "Caribbean", b: [], a: [] },
  { n: "Tuvalu", i2: "TV", i3: "TUV", r: "Oceania", s: "Polynesia", b: [], a: [] },
  { n: "Vanuatu", i2: "VU", i3: "VUT", r: "Oceania", s: "Melanesia", b: [], a: [] },
  { n: "Vatican City", i2: "VA", i3: "VAT", r: "Europe", s: "Southern Europe", b: ["ITA"], a: ["Holy See"] },
];

const CURRENCY_CODES_BY_ISO3: Record<string, string[]> = {
  AUT: ['EUR'],
  BEL: ['EUR'],
  HRV: ['EUR'],
  EST: ['EUR'],
  FIN: ['EUR'],
  FRA: ['EUR'],
  CYP: ['EUR'],
  DEU: ['EUR'],
  GRC: ['EUR'],
  IRL: ['EUR'],
  ITA: ['EUR'],
  LVA: ['EUR'],
  LTU: ['EUR'],
  MLT: ['EUR'],
  LUX: ['EUR'],
  NLD: ['EUR'],
  PRT: ['EUR'],
  SVK: ['EUR'],
  SVN: ['EUR'],
  ESP: ['EUR'],
  HKG: ['HKD'],
  USA: ['USD'],
  ECU: ['USD'],
  SLV: ['USD'],
  PAN: ['USD'],
  TLS: ['USD'],
  ZWE: ['USD'],
  MHL: ['USD'],
  FSM: ['USD'],
  PLW: ['USD'],
  AUS: ['AUD'],
  KIR: ['AUD'],
  NRU: ['AUD'],
  TUV: ['AUD', 'NZD'],
  NZL: ['NZD'],
  BEN: ['XOF'],
  BFA: ['XOF'],
  CIV: ['XOF'],
  GNB: ['XOF'],
  MLI: ['XOF'],
  NER: ['XOF'],
  SEN: ['XOF'],
  TGO: ['XOF'],
  CMR: ['XAF'],
  CAF: ['XAF'],
  TCD: ['XAF'],
  COG: ['XAF'],
  GNQ: ['XAF'],
  GAB: ['XAF'],
  ATG: ['XCD'],
  DMA: ['XCD'],
  GRD: ['XCD'],
  KNA: ['XCD'],
  LCA: ['XCD'],
  VCT: ['XCD'],
};

const CURRENCY_GROUPS_BY_ISO3: Record<string, string[]> = {
  GBR: ['pound'],
  EGY: ['pound'],
  SDN: ['pound'],
  SSD: ['pound'],
  LBN: ['pound'],
  SYR: ['pound'],
  USA: ['dollar'],
  CAN: ['dollar'],
  HKG: ['dollar'],
  AUS: ['dollar'],
  NZL: ['dollar'],
  SGP: ['dollar'],
  BRN: ['dollar'],
  BHS: ['dollar'],
  BRB: ['dollar'],
  BLZ: ['dollar'],
  JAM: ['dollar'],
  LBR: ['dollar'],
  NAM: ['dollar'],
  SUR: ['dollar'],
  TTO: ['dollar'],
  ZWE: ['dollar'],
  MEX: ['peso'],
  ARG: ['peso'],
  CHL: ['peso'],
  COL: ['peso'],
  CUB: ['peso'],
  DOM: ['peso'],
  PHL: ['peso'],
  URY: ['peso'],
  DZA: ['dinar'],
  BHR: ['dinar'],
  IRQ: ['dinar'],
  JOR: ['dinar'],
  KWT: ['dinar'],
  LBY: ['dinar'],
  SRB: ['dinar'],
  TUN: ['dinar'],
  IRN: ['rial/riyal'],
  OMN: ['rial/riyal'],
  SAU: ['rial/riyal'],
  QAT: ['rial/riyal'],
  YEM: ['rial/riyal'],
  CHE: ['franc'],
  LIE: ['franc'],
  COM: ['franc'],
  DJI: ['franc'],
};

const TRIVIA_TAG_SEEDS: Record<string, string[]> = {
  brics_original: ['BRA', 'RUS', 'IND', 'CHN', 'ZAF'],
  brics_current: ['BRA', 'RUS', 'IND', 'CHN', 'ZAF', 'EGY', 'ETH', 'IRN', 'SAU', 'ARE'],
  nato_current: [
    'ALB', 'BEL', 'BGR', 'CAN', 'HRV', 'CZE', 'DNK', 'EST', 'FIN', 'FRA',
    'DEU', 'GRC', 'HUN', 'ISL', 'ITA', 'LVA', 'LTU', 'LUX', 'MNE', 'NLD',
    'MKD', 'NOR', 'POL', 'PRT', 'ROU', 'SVK', 'SVN', 'ESP', 'SWE', 'TUR',
    'GBR', 'USA',
  ],
  un_observer_states: ['PSE', 'VAT'],
  commonwealth_realms: [
    'AUS', 'BHS', 'CAN', 'GBR', 'NZL', 'PNG', 'SLB', 'TUV', 'JAM', 'ATG',
    'BLZ', 'GRD', 'KNA', 'LCA', 'VCT',
  ],
  former_commonwealth_realms: [
    'BRB', 'FJI', 'GMB', 'GHA', 'GUY', 'IND', 'KEN', 'LKA', 'MWI', 'MUS',
    'NGA', 'PAK', 'SLE', 'TTO', 'UGA', 'TZA', 'ZAF',
  ],
  ymca_countries: [
    'ALB', 'ARM', 'AUT', 'BEL', 'BIH', 'BGR', 'BLR', 'CZE', 'DNK', 'EST',
    'FIN', 'FRA', 'DEU', 'GEO', 'GRC', 'HUN', 'ISL', 'IRL', 'ITA', 'XKX',
    'LVA', 'LTU', 'MLT', 'MDA', 'MNE', 'NLD', 'MKD', 'NOR', 'PRT', 'POL',
    'ROU', 'RUS', 'SRB', 'SVK', 'ESP', 'SWE', 'CHE', 'UKR', 'GBR',
    'ABW', 'BHS', 'BRB', 'BLZ', 'CAN', 'CYM', 'CRI', 'CUB', 'DOM', 'SLV',
    'GTM', 'HTI', 'HND', 'JAM', 'MEX', 'NIC', 'PAN', 'VCT',
    'ARG', 'BOL', 'BRA', 'CHL', 'COL', 'ECU', 'GUY', 'PRY', 'PER', 'SUR',
    'TTO', 'URY', 'VEN',
    'AGO', 'CMR', 'EGY', 'ETH', 'GMB', 'GHA', 'KEN', 'LBR', 'MDG', 'NER',
    'NGA', 'SEN', 'SLE', 'ZAF', 'TZA', 'TGO', 'ZMB', 'ZWE',
    'AUS', 'BGD', 'KHM', 'CHN', 'TLS', 'FJI', 'HKG', 'IND', 'IDN', 'JPN',
    'JOR', 'KOR', 'LBN', 'MAC', 'MYS', 'MNG', 'MMR', 'NPL', 'NZL', 'PAK',
    'PHL', 'SGP', 'LKA', 'TWN', 'THA', 'VNM',
  ],
  monarchies: [
    'AND', 'ATG', 'AUS', 'BHS', 'BHR', 'BEL', 'BLZ', 'BTN', 'BRN', 'KHM',
    'CAN', 'DNK', 'SWZ', 'GRD', 'JAM', 'JPN', 'JOR', 'KWT', 'LSO', 'LIE',
    'LUX', 'MYS', 'MAR', 'NLD', 'NZL', 'NOR', 'OMN', 'PNG', 'QAT', 'KNA',
    'LCA', 'VCT', 'SAU', 'SLB', 'ESP', 'SWE', 'THA', 'TON', 'TUV', 'ARE',
    'GBR', 'VAT',
  ],
  eu_members: [
    'AUT', 'BEL', 'BGR', 'HRV', 'CYP', 'CZE', 'DNK', 'EST', 'FIN', 'FRA',
    'DEU', 'GRC', 'HUN', 'IRL', 'ITA', 'LVA', 'LTU', 'LUX', 'MLT', 'NLD',
    'POL', 'PRT', 'ROU', 'SVK', 'SVN', 'ESP', 'SWE',
  ],
  former_ussr: [
    'ARM', 'AZE', 'BLR', 'EST', 'GEO', 'KAZ', 'KGZ', 'LVA', 'LTU', 'MDA',
    'RUS', 'TJK', 'TKM', 'UKR', 'UZB',
  ],
  national_bird_eagle: ['USA', 'MEX', 'ALB', 'POL', 'NAM', 'ZMB', 'ZWE', 'MWI', 'PHL'],
  drives_left: [
    'AUS', 'GBR', 'IRL', 'JPN', 'THA', 'MYS', 'IDN', 'NZL', 'IND', 'PAK',
    'LKA', 'NPL', 'BGD', 'SGP', 'KEN', 'ZAF',
  ],
  landlocked_subset: [
    'AFG', 'AND', 'ARM', 'AUT', 'AZE', 'BOL', 'BWA', 'BFA', 'BDI', 'CAF',
    'TCD', 'CHE', 'CZE', 'ETH', 'HUN', 'KAZ', 'KGZ', 'LSO', 'LIE', 'LUX',
    'MKD', 'MDA', 'MNG', 'NPL', 'NER', 'PRY', 'RWA', 'SMR', 'SRB', 'SVK',
    'SSD', 'SWZ', 'TJK', 'TKM', 'UGA', 'UZB', 'VAT', 'ZMB', 'ZWE',
  ],
  capital_contains_country_name: ['DJI', 'KWT', 'LUX', 'SMR', 'SGP', 'VAT', 'STP', 'MEX', 'PAN', 'GTM'],
  capital_contains_port: ['BEN', 'HTI', 'MUS', 'PNG', 'VUT', 'TTO'],
  capital_contains_saint_variant: ['GRD', 'ATG', 'CRI', 'SMR', 'SLV', 'STP'],
  capital_contains_city_variant: ['MEX', 'PAN', 'GTM', 'KWT', 'VAT', 'GNQ'],
  capital_shorter_than_country_name: [
    'AFG', 'AUS', 'AUT', 'AZE', 'BGD', 'BLR', 'BOL', 'CMR', 'COL', 'HRV',
    'FRA', 'DEU', 'IDN', 'IRL', 'ITA', 'JOR', 'LVA', 'LBN', 'LTU', 'MDV',
    'MAR', 'MOZ', 'NLD', 'NZL', 'NGA', 'NOR', 'PHL', 'PRT', 'SAU', 'CHE',
    'THA', 'TUN', 'UKR', 'ARE', 'GBR', 'USA', 'UZB', 'VEN', 'VNM', 'ZWE',
  ],
  constitutional_monarchies_europe: ['GBR', 'ESP', 'SWE', 'NOR', 'BEL'],
  elective_monarchies: ['AND', 'MYS', 'ARE', 'VAT'],
  commonwealth_realms_subset: ['AUS', 'CAN', 'NZL', 'JAM', 'BRB'],
  absolute_monarchies: ['BHR', 'SAU', 'OMN', 'SWZ'],
  co_monarchs: ['AND'],
  oil_producers: [
    'DZA', 'AGO', 'ARG', 'AUS', 'AZE', 'BHR', 'BOL', 'BRA', 'BRN', 'CMR',
    'CAN', 'TCD', 'CHN', 'COL', 'COG', 'COD', 'CIV', 'DNK', 'ECU', 'EGY',
    'GNQ', 'GAB', 'GHA', 'GTM', 'IND', 'IDN', 'IRN', 'IRQ', 'ITA', 'KAZ',
    'KWT', 'LBY', 'MYS', 'MEX', 'MMR', 'NLD', 'NGA', 'NOR', 'OMN', 'PAK',
    'PNG', 'PER', 'PHL', 'QAT', 'ROU', 'RUS', 'SAU', 'SSD', 'SDN', 'SYR',
    'THA', 'TTO', 'TUN', 'TUR', 'TKM', 'ARE', 'GBR', 'USA', 'UZB', 'VEN',
    'VNM', 'YEM', 'UGA',
  ],
  active_volcanoes: [
    'ARG', 'BOL', 'CHL', 'COL', 'CRI', 'DMA', 'ECU', 'SLV', 'GTM', 'HND',
    'MEX', 'NIC', 'PAN', 'PER', 'USA', 'CAN', 'GRD', 'KNA', 'LCA', 'VCT',
    'ISL', 'ITA', 'ESP', 'PRT', 'GRC', 'RUS', 'CMR', 'CPV', 'COM', 'COD',
    'DJI', 'ERI', 'ETH', 'KEN', 'RWA', 'TZA', 'UGA', 'STP', 'CHN', 'IND',
    'IDN', 'JPN', 'PRK', 'PHL', 'PNG', 'SLB', 'TON', 'VUT', 'NZL', 'FJI',
    'YEM',
  ],
  religious_head_of_state: ['VAT', 'IRN'],
  no_rivers: ['ARE', 'CYP', 'DJI', 'KNA', 'MHL', 'OMN', 'QAT', 'TUV', 'VAT'],
  enclaves_within_country: ['LSO', 'SMR', 'VAT'],
  contains_enclaves: ['ITA', 'ZAF'],
  new_7_wonders_2007: ['BRA', 'PER', 'MEX', 'JOR', 'ITA', 'CHN', 'IND'],
  new_7_wonders_nature: ['ARG', 'BRA', 'IDN', 'KOR', 'VNM', 'PHL', 'ZAF'],
  ancient_wonders_modern_countries: ['EGY', 'IRQ', 'TUR', 'GRC'],
  wonders_near_coast: ['BRA', 'VNM', 'KOR', 'PHL', 'ZAF'],
  asian_countries_with_wonders: ['CHN', 'IND', 'JOR', 'IDN', 'KOR', 'VNM', 'PHL'],
  flag_horizontal_symmetry: [
    'ARM', 'AUT', 'BWA', 'BGR', 'CRI', 'EST', 'GAB', 'GMB', 'DEU', 'HUN',
    'IDN', 'ISR', 'JPN', 'LUX', 'NLD', 'NGA', 'POL', 'RUS', 'SLE', 'CHE',
    'THA', 'UKR',
  ],
  no_native_snakes: ['ISL', 'IRL', 'NZL', 'CPV', 'MLT', 'KIR', 'NRU', 'TUV', 'MHL', 'FSM'],
  flag_vertical_symmetry: [
    'AUT', 'BEL', 'CAN', 'FRA', 'GEO', 'GTM', 'ITA', 'JPN', 'MLI', 'NGA',
    'PAK', 'PER', 'SEN', 'CHE', 'THA',
  ],
  no_native_bears: [
    'AUS', 'NZL', 'ZAF', 'NGA', 'KEN', 'EGY', 'MDG', 'IDN', 'PHL', 'GBR',
    'IRL', 'ISL', 'BRA', 'ARG', 'CHL',
  ],
  flag_rotational_symmetry: [
    'JPN', 'CHE', 'GBR', 'AUT', 'BWA', 'ISR', 'LAO', 'FSM', 'PLW', 'SYC', 'GEO',
  ],
  no_native_scorpions: ['ISL', 'IRL', 'GBR', 'NZL', 'FIN', 'SWE', 'NOR', 'EST', 'LVA', 'LTU'],
  flag_horizontal_stripes_only: [
    'ARM', 'AUT', 'BGR', 'EST', 'DEU', 'GAB', 'HUN', 'IDN', 'LUX', 'NLD',
    'POL', 'RUS', 'SLE', 'UKR', 'YEM', 'LTU', 'CIV',
  ],
  flag_nordic_cross: ['DNK', 'FIN', 'ISL', 'NOR', 'SWE'],
};

const FRACTIONAL_UNITS_BY_ISO3: Record<string, { units: string[]; inUse: boolean | null }> = {
  USA: { units: ['cent'], inUse: true },
  CAN: { units: ['cent'], inUse: true },
  AUS: { units: ['cent'], inUse: true },
  NZL: { units: ['cent'], inUse: true },
  ZAF: { units: ['cent'], inUse: true },
  HKG: { units: ['cent'], inUse: true },
  SGP: { units: ['cent'], inUse: true },
  MYS: { units: ['cent', 'sen'], inUse: true },
  BRN: { units: ['cent'], inUse: true },
  FJI: { units: ['cent'], inUse: true },
  NAM: { units: ['cent'], inUse: true },
  SUR: { units: ['cent'], inUse: true },
  TTO: { units: ['cent'], inUse: true },
  GBR: { units: ['pence'], inUse: true },
  MEX: { units: ['centavo'], inUse: true },
  BRA: { units: ['centavo'], inUse: true },
  ARG: { units: ['centavo'], inUse: true },
  CHL: { units: ['centavo'], inUse: false },
  COL: { units: ['centavo'], inUse: true },
  PER: { units: ['centavo'], inUse: true },
  PHL: { units: ['centavo'], inUse: true },
  CUB: { units: ['centavo'], inUse: true },
  DOM: { units: ['centavo'], inUse: true },
  VEN: { units: ['centavo'], inUse: true },
  JPN: { units: ['sen'], inUse: false },
  IDN: { units: ['sen'], inUse: true },
  KOR: { units: [], inUse: false },
  VNM: { units: [], inUse: false },
  ISL: { units: [], inUse: false },
};

const NO_OWN_CURRENCY = new Set([
  'AND',
  'SMR',
  'VAT',
  'MNE',
  'XKX',
  'PAN',
  'ECU',
  'SLV',
  'TLS',
  'LIE',
  'MHL',
  'FSM',
  'PLW',
  'KIR',
  'NRU',
  'TUV',
]);

const CAPITALS_BY_ISO3: Record<string, string> = {
  USA: 'Washington, D.C.',
  CAN: 'Ottawa',
  MEX: 'Mexico City',
  BRA: 'Brasília',
  ARG: 'Buenos Aires',
  GBR: 'London',
  FRA: 'Paris',
  DEU: 'Berlin',
  ITA: 'Rome',
  ESP: 'Madrid',
  CHN: 'Beijing',
  JPN: 'Tokyo',
  IND: 'New Delhi',
  AUS: 'Canberra',
  NZL: 'Wellington',
  ZAF: 'Pretoria',
  EGY: 'Cairo',
  KEN: 'Nairobi',
  RUS: 'Moscow',
  TUR: 'Ankara',
  SWE: 'Stockholm',
  NOR: 'Oslo',
  CHE: 'Bern',
  POL: 'Warsaw',
  THA: 'Bangkok',
  VNM: 'Hanoi',
  IDN: 'Jakarta',
  SAU: 'Riyadh',
  ISR: 'Jerusalem',
  CHL: 'Santiago',
  PRT: 'Lisbon',
  IRL: 'Dublin',
  NLD: 'Amsterdam',
  BEL: 'Brussels',
  AUT: 'Vienna',
  CZE: 'Prague',
  SVK: 'Bratislava',
  HUN: 'Budapest',
  ROU: 'Bucharest',
  BGR: 'Sofia',
  GRC: 'Athens',
  MAR: 'Rabat',
  DZA: 'Algiers',
  TUN: 'Tunis',
  NGA: 'Abuja',
  GHA: 'Accra',
  ETH: 'Addis Ababa',
  KOR: 'Seoul',
  PRK: 'Pyongyang',
  PAK: 'Islamabad',
  AFG: 'Kabul',
  BGD: 'Dhaka',
  LKA: 'Sri Jayawardenepura Kotte',
  NPL: 'Kathmandu',
  LBN: 'Beirut',
  JOR: 'Amman',
  QAT: 'Doha',
  ARE: 'Abu Dhabi',
  OMN: 'Muscat',
  KWT: 'Kuwait City',
  IRN: 'Tehran',
  IRQ: 'Baghdad',
  SYR: 'Damascus',
  PHL: 'Manila',
  SGP: 'Singapore',
  MYS: 'Kuala Lumpur',
  LIE: 'Vaduz',
  ISL: 'Reykjavík',
  FIN: 'Helsinki',
  DNK: 'Copenhagen',
  PSE: 'Ramallah',
  CUB: 'Havana',
  HTI: 'Port-au-Prince',
  DOM: 'Santo Domingo',
  JAM: 'Kingston',
};

export const loadData = async (): Promise<GameData> => {
  if (cachedData) return cachedData;

  // Transform raw data into canonical Country objects
  const countries: Country[] = RAW_COUNTRIES.map(raw => {
    const currencyCodes = CURRENCY_CODES_BY_ISO3[raw.i3] ?? [];
    const currencyGroupNames = CURRENCY_GROUPS_BY_ISO3[raw.i3] ?? [];
    const fractionalInfo = FRACTIONAL_UNITS_BY_ISO3[raw.i3] ?? { units: [], inUse: null };
    const issuesOwnCurrency = !NO_OWN_CURRENCY.has(raw.i3);

    return {
      iso2: raw.i2,
      iso3: raw.i3,
      name: raw.n,
      capital: CAPITALS_BY_ISO3[raw.i3] ?? null,
      nativeNames: raw.a, // Simplified for this demo
      altSpellings: raw.a,
      region: raw.r,
      subregion: raw.s,
      borders: raw.b,
      flagUrl: getFlagUrl(raw.i2),
      currencyCodes,
      currencyGroupNames,
      fractionalUnits: fractionalInfo.units,
      fractionalUnitInUse: fractionalInfo.inUse,
      issuesOwnCurrency,
    };
  });

  // PHASE 3: Build Derived Indexes
  const byIso3: Record<string, Country> = {};
  const byRegion: Record<string, string[]> = {};
  const byFirstLetter: Record<string, string[]> = {};
  const byCapital: Record<string, string[]> = {};
  const landlocked: string[] = [];
  const byCurrencyCode: Record<string, string[]> = {};
  const byCurrencyGroup: Record<string, string[]> = {};
  const byFractionalUnit: Record<string, string[]> = {};
  const noFractionalUnit: string[] = [];
  const noOwnCurrency: string[] = [];
  const byTriviaTag: Record<string, string[]> = {};

  countries.forEach(c => {
    byIso3[c.iso3] = c;

    // Region Index
    if (!byRegion[c.region]) byRegion[c.region] = [];
    byRegion[c.region].push(c.iso3);

    // Letter Index
    const firstLetter = c.name.charAt(0).toUpperCase();
    if (!byFirstLetter[firstLetter]) byFirstLetter[firstLetter] = [];
    byFirstLetter[firstLetter].push(c.iso3);

    if (c.capital) {
      if (!byCapital[c.capital]) byCapital[c.capital] = [];
      byCapital[c.capital].push(c.iso3);
    }

    // Landlocked Index (No borders is usually island, but for simplification we verify borders count > 0 is not sufficient, 
    // but in this game context, we might ask "Has no borders" for islands.
    // Real landlocked calc requires full world graph, but we can do a simple "Is Landlocked" question if we had the prop.
    // For this demo, let's index by having > 0 borders)
    // Actually, let's index "Islands" (0 borders) vs "Land"

    c.currencyCodes.forEach((code) => {
      if (!byCurrencyCode[code]) byCurrencyCode[code] = [];
      byCurrencyCode[code].push(c.iso3);
    });

    c.currencyGroupNames.forEach((group) => {
      if (!byCurrencyGroup[group]) byCurrencyGroup[group] = [];
      byCurrencyGroup[group].push(c.iso3);
    });

    c.fractionalUnits.forEach((unit) => {
      if (!byFractionalUnit[unit]) byFractionalUnit[unit] = [];
      byFractionalUnit[unit].push(c.iso3);
    });

    if (c.fractionalUnitInUse === false) {
      noFractionalUnit.push(c.iso3);
    }

    if (!c.issuesOwnCurrency) {
      noOwnCurrency.push(c.iso3);
    }
  });

  Object.entries(TRIVIA_TAG_SEEDS).forEach(([tag, values]) => {
    byTriviaTag[tag] = values.filter((iso3) => Boolean(byIso3[iso3]));
  });

  const usdLegalTender = countries
    .filter((country) => country.currencyCodes.includes('USD'))
    .map((country) => country.iso3);
  byTriviaTag.usd_legal_tender = usdLegalTender;

  const westAfricanCfa = countries
    .filter((country) => country.currencyCodes.includes('XOF'))
    .map((country) => country.iso3);
  const centralAfricanCfa = countries
    .filter((country) => country.currencyCodes.includes('XAF'))
    .map((country) => country.iso3);
  byTriviaTag.cfa_franc = [...westAfricanCfa, ...centralAfricanCfa];
  byTriviaTag.west_african_cfa = westAfricanCfa;
  byTriviaTag.central_african_cfa = centralAfricanCfa;

  const unObserverStates = new Set(byTriviaTag.un_observer_states ?? []);
  const nonMembers = new Set(['TWN', 'XKX']);
  byTriviaTag.un_members = countries
    .map((country) => country.iso3)
    .filter((iso3) => !unObserverStates.has(iso3) && !nonMembers.has(iso3));

  byTriviaTag.one_land_border = countries
    .filter((country) => country.borders.length === 1)
    .map((country) => country.iso3);
  byTriviaTag.five_or_more_land_borders = countries
    .filter((country) => country.borders.length >= 5)
    .map((country) => country.iso3);

  const saintPattern = /^(saint|st\\.?|san|sao|são|santa|sainte)\\b/i;
  byTriviaTag.named_after_saint = countries
    .filter((country) => saintPattern.test(country.name))
    .map((country) => country.iso3);
  byTriviaTag.name_contains_and = countries
    .filter((country) => /\\band\\b/i.test(country.name))
    .map((country) => country.iso3);
  byTriviaTag.name_contains_z_not_start = countries
    .filter((country) => /z/i.test(country.name) && !/^z/i.test(country.name))
    .map((country) => country.iso3);
  byTriviaTag.name_ends_with_stan = countries
    .filter((country) => /stan$/i.test(country.name.trim()))
    .map((country) => country.iso3);
  byTriviaTag.name_contains_land = countries
    .filter((country) => /land/i.test(country.name))
    .map((country) => country.iso3);
  byTriviaTag.name_multiple_words = countries
    .filter((country) => country.name.trim().split(/\\s+/).length >= 2)
    .map((country) => country.iso3);
  byTriviaTag.name_three_or_more_words = countries
    .filter((country) => country.name.trim().split(/\\s+/).length >= 3)
    .map((country) => country.iso3);

  cachedData = {
    countries,
    indexes: {
      byIso3,
      byRegion,
      byFirstLetter,
      byCapital,
      landlocked, // Leaving empty as simplified dataset doesn't explicitly mark landlocked, but we have borders.
      byCurrencyCode,
      byCurrencyGroup,
      byFractionalUnit,
      noFractionalUnit,
      noOwnCurrency,
      byTriviaTag,
    }
  };

  return cachedData;
};

export const getGameData = (): GameData => {
  if (!cachedData) throw new Error("Data not loaded");
  return cachedData;
};
